import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "api-store/store";
import {
  MidContainer,
  PageDescription,
  PageTitle,
  PageTopSection,
  PageWrapper,
} from "utility/styled-components";
import {
  TeamDirectoryLeft,
  TeamDirectoryMember,
  TeamDirectoryMemberDesignation,
  TeamDirectoryMemberName,
  TeamDirectoryMemberProfile,
  TeamDirectoryMemberProfileInfo,
  TeamDirectoryRight,
  TeamDirectoryRightCard,
  TeamDirectoryRightCardProfile,
  TeamDirectoryRightCardProfileDesignation,
  TeamDirectoryRightCardProfileInfo,
  TeamDirectoryRightCardProfileName,
  TeamDirectoryRightCardProfiles,
  TeamDirectoryRightCardTitle,
  TeamDirectorySectionItem,
  TeamDirectorySectionTitle,
  TeamDirectoryWrapper,
} from "./style";
import { TEAM_DIRECTORY_CONSTANT } from "utility/constants";
import { fetchTeamDirectoryRequest } from "./ducks/slice";
import { TeamMember, TeamSection } from "./types";
import { CircularProgress } from "@mui/material";

function TeamDirectory() {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading, error, fetchAttempted } = useSelector(
    (state: RootState) => state.teamDirectory
  );

  useEffect(() => {
    if (!fetchAttempted && !loading) {
      dispatch(fetchTeamDirectoryRequest());
    }
  }, [dispatch, fetchAttempted, loading]);

  const renderInitials = (name: string) => {
    const parts = name.split(" ");
    return (
      <span>
        {parts[0]?.slice(0, 1)}
        {parts[1]?.slice(0, 1)}
      </span>
    );
  };

  const renderMemberProfile = (member: TeamMember) => {
    return (
      <TeamDirectoryMemberProfile className={!member.profileImage ? "no-image" : ""}>
        {member.profileImage ? (
          <img src={member.profileImage} alt={member.name} width={50} height={50} />
        ) : (
          renderInitials(member.name)
        )}
      </TeamDirectoryMemberProfile>
    );
  };

  if (loading) {
    return (
      <PageWrapper>
        <MidContainer>
          <PageTopSection>
            <PageTitle>{TEAM_DIRECTORY_CONSTANT.title}</PageTitle>
          </PageTopSection>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "60px" }}>
            <CircularProgress />
          </div>
        </MidContainer>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <MidContainer>
          <PageTopSection>
            <PageTitle>{TEAM_DIRECTORY_CONSTANT.title}</PageTitle>
          </PageTopSection>
          <div style={{ textAlign: "center", marginTop: "60px", color: "#666" }}>
            Failed to load team directory. Please try again later.
          </div>
        </MidContainer>
      </PageWrapper>
    );
  }

  const leftSections = (data || [])
    .filter((s: TeamSection) => s.side === "left")
    .sort((a: TeamSection, b: TeamSection) => a.order - b.order);

  const rightSections = (data || [])
    .filter((s: TeamSection) => s.side === "right")
    .sort((a: TeamSection, b: TeamSection) => a.order - b.order);

  return (
    <PageWrapper>
      <MidContainer>
        <PageTopSection>
          <PageTitle>{TEAM_DIRECTORY_CONSTANT.title}</PageTitle>
          <PageDescription>
            {TEAM_DIRECTORY_CONSTANT.description}
          </PageDescription>
        </PageTopSection>
        <TeamDirectoryWrapper>
          <TeamDirectoryLeft>
            {leftSections.map((section: TeamSection, index: number) => (
              <div key={index + 1}>
                <TeamDirectorySectionTitle>
                  {section.sectionTitle}
                </TeamDirectorySectionTitle>
                <TeamDirectorySectionItem>
                  {[...section.members]
                    .sort((a, b) => a.order - b.order)
                    .map((member: TeamMember, mIndex: number) => (
                      <TeamDirectoryMember key={mIndex + 1}>
                        {renderMemberProfile(member)}
                        <TeamDirectoryMemberProfileInfo>
                          <TeamDirectoryMemberName>
                            {member.name}
                          </TeamDirectoryMemberName>
                          {member.isDesignationLink ? (
                            <a href={`mailto:${member.designation}`}>
                              <TeamDirectoryMemberDesignation>
                                {member.designation}
                              </TeamDirectoryMemberDesignation>
                            </a>
                          ) : (
                            <TeamDirectoryMemberDesignation>
                              {member.designation}
                            </TeamDirectoryMemberDesignation>
                          )}
                          {member.email && (
                            <a href={`mailto:${member.email}`}>
                              <TeamDirectoryMemberDesignation>
                                {member.email}
                              </TeamDirectoryMemberDesignation>
                            </a>
                          )}
                        </TeamDirectoryMemberProfileInfo>
                      </TeamDirectoryMember>
                    ))}
                </TeamDirectorySectionItem>
              </div>
            ))}
          </TeamDirectoryLeft>
          <TeamDirectoryRight>
            {rightSections.map((section: TeamSection, index: number) => (
              <TeamDirectoryRightCard key={index + 1}>
                <TeamDirectoryRightCardTitle>
                  {section.sectionTitle}
                </TeamDirectoryRightCardTitle>
                <TeamDirectoryRightCardProfiles>
                  {[...section.members]
                    .sort((a, b) => a.order - b.order)
                    .map((member: TeamMember, mIndex: number) => (
                      <TeamDirectoryRightCardProfile key={mIndex + 1}>
                        {renderMemberProfile(member)}
                        <TeamDirectoryRightCardProfileInfo>
                          <TeamDirectoryRightCardProfileName>
                            {member.name}
                          </TeamDirectoryRightCardProfileName>
                          <TeamDirectoryRightCardProfileDesignation>
                            {member.designation}
                          </TeamDirectoryRightCardProfileDesignation>
                        </TeamDirectoryRightCardProfileInfo>
                      </TeamDirectoryRightCardProfile>
                    ))}
                </TeamDirectoryRightCardProfiles>
              </TeamDirectoryRightCard>
            ))}
          </TeamDirectoryRight>
        </TeamDirectoryWrapper>
      </MidContainer>
    </PageWrapper>
  );
}

export default TeamDirectory;
